import { TranslationService } from '../../../services/translation.service';
import {
	DatacenterDateFilter,
	FilterType,
	SuggestiveListFilter,
	FilterApiRequest,
	DropdownFilter,
	DatacenterFreeTextFilter,
	ShipmentOrderNumberFilter,
	GlobalSearchFilter,
	SuggestiveListWithIdFilter
} from '../../../models/datacenter-filters';
import { ORDER_SERVER_URL } from '../../../../config';

const GET_SHIPMENTS_URL = `${ ORDER_SERVER_URL }/v4/shipments`;
const GET_SHIPMENT_V4_URL = `${ ORDER_SERVER_URL }/v4/dataCenterShipments`;
const GET_DATACENTER_ORDERS_URL = `${ ORDER_SERVER_URL }/v4/dataCenterOrders`;
const GET_SCHEDULE_BUSINESS_ORDER_URL = `${ ORDER_SERVER_URL }/v3/scheduleBusinessOrders`;
const GET_DATACENTER_BATCHES_URL_V5 = `${ ORDER_SERVER_URL }/v5/datacenterBatches`;

type FilterTypeFn = () => FilterType;

export class ShipmentFiltersConfiguration {
	constructor(
		private readonly translationService: TranslationService
	) {}

	// eslint-disable-next-line @typescript-eslint/naming-convention
	SHIPMENTS_DATACENTER_FILTERS = () =>
		new Map<string, FilterTypeFn>([
			[
				'shipTo',
				() => new SuggestiveListFilter(
					'shipTo',
					this.translationService.getTR('shipment.filter.ship-to'),
					this.translationService.getTR('shipment.filter.ship-to'),
					'shipTo',
					'shipToDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500, distinct: true })
				)
			],
			[
				'loadingProgramNumber',
				() => new DatacenterFreeTextFilter(
					'loadingProgramNumber',
					this.translationService.getTR('datacenter-filter.loading-program-number'),
					this.translationService.getTR('datacenter-filter.loading-program-number'),
					'loadingProgramNumber',
				)
			],
			[
				'transportMode',
				() => new DropdownFilter(
					'transportMode',
					this.translationService.getTR('shipment.filter.transport-mode'),
					this.translationService.getTR('shipment.filter.transport-mode'),
					'transportMode',
					'transportModeDescription',
					new FilterApiRequest(GET_SHIPMENTS_URL, { limit: 1500 })
				)
			],
			[
				'soldTo',
				() => new SuggestiveListFilter(
					'soldTo',
					this.translationService.getTR('shipment.filter.customer'),
					this.translationService.getTR('shipment.filter.customer'),
					'soldTo',
					'soldToDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500, distinct: true })
				)
			],
			[
				'dispatchAdviceDate',
				() => new DatacenterDateFilter(
					'dispatchAdviceDate',
					this.translationService.getTR('shipment.filter.dispatch-advice-date'),
					this.translationService.getTR('filter.date.from'),
					this.translationService.getTR('filter.date.to'),
					'dispatchAdviceDate',
					'week'
				)
			],
			[
				'mill',
				() => new SuggestiveListFilter(
					'mill',
					this.translationService.getTR('shipment.filter.mill'),
					this.translationService.getTR('shipment.filter.mill'),
					'mill',
					'millDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500, distinct: true })
				)
			],
			[
				'transportReferenceConcat',
				() => new DatacenterFreeTextFilter(
					'transportReferenceConcat',
					this.translationService.getTR('datacenter-shipment.transport-reference'),
					this.translationService.getTR('datacenter-shipment.transport-reference'),
					'transportReferenceConcat'
				)
			],
			[
				'carrierNameConcat',
				() => new DropdownFilter(
					'carrierNameConcat',
					this.translationService.getTR('datacenter-filter.carrier-name'),
					this.translationService.getTR('datacenter-filter.carrier-name'),
					'carrierNameConcat',
					'carrierNameConcat',
					new FilterApiRequest(GET_DATACENTER_BATCHES_URL_V5, { limit: 1500 })
				)
			],
			[
				'supplierOrderAndItemConcat',
				() => new ShipmentOrderNumberFilter(
					'supplierOrderAndItemConcat',
					this.translationService.getTR('order.supplier.order.number'),
					this.translationService.getTR('filter.order-number.reference'),
					this.translationService.getTR('filter.order-number.item'),
					'supplierOrderAndItemConcat',
				)
			],
			[
				'customerOrderAndItemConcat',
				() => new ShipmentOrderNumberFilter(
					'customerOrderAndItemConcat',
					this.translationService.getTR('order.customer.order.number'),
					this.translationService.getTR('filter.order-number.reference'),
					this.translationService.getTR('filter.order-number.item'),
					'customerOrderAndItemConcat',
				)
			],
			[
				'batchReferenceConcat',
				() => new DatacenterFreeTextFilter(
					'batchReferenceConcat',
					this.translationService.getTR('datacenter-shipment.batch'),
					this.translationService.getTR('datacenter-shipment.batch'),
					'batchReferenceConcat'
				)
			],
			[
				'customerArticleReferenceConcat',
				() => new DatacenterFreeTextFilter(
					'customerArticleReferenceConcat',
					this.translationService.getTR('filter.customerArticleReference'),
					this.translationService.getTR('filter.customerArticleReference'),
					'customerArticleReferenceConcat'
				)
			],
			[
				'dispatchAdviceNumber',
				() => new DatacenterFreeTextFilter(
					'dispatchAdviceNumber',
					this.translationService.getTR('datacenter-shipment.dispatch-advice-number'),
					this.translationService.getTR('datacenter-shipment.dispatch-advice-number'),
					'dispatchAdviceNumber'
				)
			],
			[
				'lengthDimensionConcat',
				() => new DatacenterFreeTextFilter(
					'lengthDimensionConcat',
					this.translationService.getTR('batch-card.length'),
					this.translationService.getTR('batch-card.length'),
					'lengthDimensionConcat'
				)
			],
			[
				'supplierOrderTypeConcat',
				() => new DropdownFilter(
					'supplierOrderTypeConcat',
					this.translationService.getTR('datacenter-filter.spot-or-schedule'),
					this.translationService.getTR('datacenter-filter.spot-or-schedule'),
					'supplierOrderTypeConcat',
					'supplierOrderTypeConcat',
					new FilterApiRequest(GET_SHIPMENT_V4_URL, { limit: 1500 }),
					[],
					undefined,
					new Map<string, string[]>([['spot', ['C']], ['scheduled', ['E', 'F']]]),
					true,
					undefined,
					false,
					true
				)
			],
			[
				'cdd',
				() => new DatacenterDateFilter(
					'cdd',
					this.translationService.getTR('order.cdd-week'),
					this.translationService.getTR('filter.date.from'),
					this.translationService.getTR('filter.date.to'),
					'cddWeek',
					'week'
				)
			],
			[
				'status',
				() => new DropdownFilter(
					'status',
					this.translationService.getTR('datacenter-filter.batch-status'),
					this.translationService.getTR('datacenter-filter.batch-status'),
					'status',
					'statusDescription',
					new FilterApiRequest(GET_DATACENTER_BATCHES_URL_V5, { limit: 1500, distinct: true })
				)
			],
			[
				'salesQuarterConcat',
				() => new DatacenterDateFilter(
					'salesQuarterConcat',
					this.translationService.getTR('datacenter-filter.sales-quarter'),
					this.translationService.getTR('filter.date.from'),
					this.translationService.getTR('filter.date.to'),
					'salesQuarterConcat',
					'quarter'
				)
			],
			[
				'currentLocationConcat',
				() => new DatacenterFreeTextFilter(
					'currentLocationConcat',
					this.translationService.getTR('datacenter-filter.current-location'),
					this.translationService.getTR('datacenter-filter.current-location'),
					'currentLocationDescriptionConcat'
				)
			],
			[
				'choice',
				() => new DropdownFilter(
					'choice',
					this.translationService.getTR('datacenter-filter.choice'),
					this.translationService.getTR('datacenter-filter.choice'),
					'choice',
					'choiceDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500, distinct: true })
				)
			],
			[
				'endUser',
				() => new SuggestiveListFilter(
					'endUser',
					this.translationService.getTR('datacenter-filter.end-user'),
					this.translationService.getTR('datacenter-filter.end-user'),
					'endUser',
					'endUserDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500, distinct: true })
				)
			],
			[
				'productCategory',
				() => new DropdownFilter(
					'productCategory',
					this.translationService.getTR('datacenter-filter.product-category'),
					this.translationService.getTR('datacenter-filter.product-category'),
					'productCategory',
					'productCategoryDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'productDescriptionStu',
				() => new DatacenterFreeTextFilter(
					'productDescriptionStu',
					this.translationService.getTR('datacenter-filter.product-description'),
					this.translationService.getTR('datacenter-filter.product-description'),
					'productDescriptionStu'
				)
			],
			[
				'readyToShipDateConcat',
				() => new DatacenterDateFilter(
					'readyToShipDateConcat',
					this.translationService.getTR('datacenter-filter.ready-to-ship-date'),
					this.translationService.getTR('filter.date.from'),
					this.translationService.getTR('filter.date.to'),
					'readyToShipDateConcat',
					'date'
				)
			],
			[
				'stockPartner',
				() => new SuggestiveListWithIdFilter(
					'stockPartner',
					this.translationService.getTR('datacenter-filter.special-stock-partner'),
					this.translationService.getTR('datacenter-filter.special-stock-partner'),
					'stockPartner',
					'stockPartnerDescription',
					new FilterApiRequest(GET_SCHEDULE_BUSINESS_ORDER_URL, { limit: 1500, distinct: true })
				)
			],
			[
				'form',
				() => new DropdownFilter(
					'form',
					this.translationService.getTR('datacenter-filter.form'),
					this.translationService.getTR('datacenter-filter.form'),
					'form',
					'formDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'supplierInvoiceNumberConcat',
				() => new DatacenterFreeTextFilter(
					'supplierInvoiceNumberConcat',
					this.translationService.getTR('batch.supplier.invoice.number'),
					this.translationService.getTR('batch.supplier.invoice.number'),
					'supplierInvoiceNumberConcat',
				)
			],
			[
				'htsCodeConcat',
				() => new DatacenterFreeTextFilter(
					'htsCodeConcat',
					this.translationService.getTR('datacenter-filter.hts-code'),
					this.translationService.getTR('datacenter-filter.hts-code'),
					'htsCodeConcat',
				)
			],
			[
				'producedLengthDimensionConcat',
				() => new DatacenterFreeTextFilter(
					'producedLengthDimensionConcat',
					this.translationService.getTR('datacenter-filter.producedLength'),
					this.translationService.getTR('datacenter-filter.producedLength'),
					'producedLengthDimensionConcat',
				)
			],
			[
				'deliveryModeConcat',
				() => new DropdownFilter(
					'deliveryModeConcat',
					this.translationService.getTR('datacenter-filter.delivery-mode'),
					this.translationService.getTR('datacenter-filter.delivery-mode'),
					'deliveryMode',
					'deliveryModeDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'serviceLevelConcat',
				() => new DropdownFilter(
					'serviceLevelConcat',
					this.translationService.getTR('datacenter-filter.service-level'),
					this.translationService.getTR('datacenter-filter.service-level'),
					'serviceLevel',
					'serviceLevelDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 })
				)
			],
			[
				'globalSearch',
				() => new GlobalSearchFilter(
					'globalSearch',
					this.translationService.getTR('global.search-filter'),
					this.translationService.getTR('global.search-filter'),
					[
						'dispatchAdviceNumber',
						'transportReferenceConcat'
					]
				)
			],
		]);

	// eslint-disable-next-line @typescript-eslint/naming-convention
	SHIPMENTS_DATACENTER_DEFAULT_FILTERS = () =>
		new Map<string, FilterTypeFn>([
			[
				'mill',
				() => new SuggestiveListFilter(
					'mill',
					this.translationService.getTR('shipment.filter.mill'),
					this.translationService.getTR('shipment.filter.mill'),
					'mill',
					'millDescription',
					new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500, distinct: true }),
					[{}]
				)
			],
			[
				'customerOrderAndItemConcat',
				() => new ShipmentOrderNumberFilter(
					'customerOrderAndItemConcat',
					this.translationService.getTR('order.customer.order.number'),
					this.translationService.getTR('filter.order-number.reference'),
					this.translationService.getTR('filter.order-number.item'),
					'customerOrderAndItemConcat',
					[{}]
				)
			],
			[
				'supplierOrderAndItemConcat',
				() => new ShipmentOrderNumberFilter(
					'supplierOrderAndItemConcat',
					this.translationService.getTR('order.supplier.order.number'),
					this.translationService.getTR('filter.order-number.reference'),
					this.translationService.getTR('filter.order-number.item'),
					'supplierOrderAndItemConcat',
					[{}]
				)
			],
			[
				'batchReferenceConcat',
				() => new DatacenterFreeTextFilter(
					'batchReferenceConcat',
					this.translationService.getTR('datacenter-shipment.batch'),
					this.translationService.getTR('datacenter-shipment.batch'),
					'batchReferenceConcat',
					['']
				)
			],
			[
				'dispatchAdviceDate',
				() => new DatacenterDateFilter(
					'dispatchAdviceDate',
					this.translationService.getTR('shipment.filter.dispatch-advice-date'),
					this.translationService.getTR('filter.date.from'),
					this.translationService.getTR('filter.date.to'),
					'dispatchAdviceDate',
					'week',
					[{ from: '', to: '' }]
				)
			],
			[
				'transportReferenceConcat',
				() => new DatacenterFreeTextFilter(
					'transportReferenceConcat',
					this.translationService.getTR('datacenter-shipment.transport-reference'),
					this.translationService.getTR('datacenter-shipment.transport-reference'),
					'transportReferenceConcat',
					['']
				)
			]
		]);
}
